export interface TokenResponse {
  accessToken: string;
  refreshToken?: string | null;
  expiresIn: number;
}

export interface UserResponse {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  phone?: string;
  avatarUrl?: string;
}

export const USER_ROLES = ["ADMIN", "MANAGER", "TRAINER", "PROFESSOR", "CLIENT"] as const;

export type UserRole = (typeof USER_ROLES)[number];

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: "Administrador",
  MANAGER: "Gerente",
  TRAINER: "Treinador",
  PROFESSOR: "Professor",
  CLIENT: "Aluno",
};

export function isUserRole(role: unknown): role is UserRole {
  return typeof role === "string" && USER_ROLES.includes(role as UserRole);
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
}

export type Modality = "JIU_JITSU" | "BOXE_KICKBOXING" | "CAPOEIRA" | "MMA";

export type VideoProvider = "YOUTUBE" | "VIMEO" | "EXTERNAL" | "INTERNAL";

export type VideoLessonStatus = "PROCESSING" | "READY" | "FAILED";

export interface VideoLesson {
  id: string;
  title: string;
  description: string | null;
  modality: Modality;
  videoUrl?: string;
  streamUrl?: string;
  status?: VideoLessonStatus;
  isActive?: boolean;
  embedUrl: string | null;
  provider: VideoProvider;
  minimumPlanRank: 1 | 2 | 3;
  professorId: string | null;
  professorName: string | null;
  createdById: string;
  createdByName: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertVideoLessonRequest {
  title: string;
  description?: string;
  modality: Modality;
  minimumPlanRank: 1 | 2 | 3;
  professorId?: string;
  active?: boolean;
}

export interface ProfessorSummary {
  id: string;
  name: string;
  email: string;
  modalities: Modality[];
}

export interface ProfessorAssignment {
  id: string;
  professorId: string;
  professorName: string;
  professorEmail: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  modality: Modality;
  notes: string | null;
  active: boolean;
  createdAt: string;
  deactivatedAt: string | null;
}

export interface AssignedProfessorStudent {
  studentId: string;
  studentName: string;
  studentEmail: string;
  modality: Modality;
  notes: string | null;
  assignedAt: string;
  planName?: string | null;
  membershipStatus?: string | null;
}

export interface PromoteProfessorRequest {
  email: string;
  modalities: Modality[];
}

export interface UpdateProfessorModalitiesRequest {
  modalities: Modality[];
}

export interface CreateProfessorAssignmentRequest {
  professorId: string;
  studentId: string;
  modality: Modality;
  notes?: string;
}

export interface Belt {
  id: string;
  name: string;
  colorHex: string;
  rankOrder: number;
}

export interface StudentProfile {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  beltId: string | null;
  beltName: string | null;
  beltColorHex: string | null;
  trainingDays: string | null;
  emergencyContact: string | null;
  emergencyPhone: string | null;
  medicalNotes: string | null;
  recoveryNotes: string | null;
  goals: string | null;
  observations: string | null;
  isActive: boolean;
}

export type StaffStudentProfile = Omit<
  StudentProfile,
  "userEmail" | "emergencyContact" | "emergencyPhone" | "medicalNotes" | "recoveryNotes"
>;

export interface AttendanceRecord {
  id: string;
  studentId: string;
  studentName: string;
  date: string;
  present: boolean;
  classId: string | null;
  notes: string | null;
}

export interface Membership {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  planId: string;
  planName: string;
  plan?: Plan | null;
  startDate: string;
  endDate: string;
  status:
    | "ACTIVE"
    | "EXPIRED"
    | "CANCELLED"
    | "SUSPENDED"
    | "PENDING_PAYMENT"
    | "PENDING_APPROVAL"
    | "REJECTED";
  autoRenew: boolean;
  stripePayment: boolean;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean | null;
}

export interface AdminStudent {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  planId?: string | null;
  planName?: string | null;
  planPrice?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  status:
    | "ACTIVE"
    | "EXPIRED"
    | "CANCELLED"
    | "SUSPENDED"
    | "PENDING_PAYMENT"
    | "PENDING_APPROVAL"
    | "REJECTED"
    | "REGISTERED";
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DeactivateStudentRequest {
  reason: string;
}

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency?: string;
  durationDays: number;
  maxClasses: number | null;
  features?: string[];
  level?: string | null;
  instructor?: string | null;
  schedule?: string[] | null;
  popular?: boolean;
  isActive: boolean;
  stripeCheckoutAvailable: boolean;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface GymClass {
  id: string;
  trainerId?: string;
  trainerName?: string;
  name: string;
  type?: string;
  description?: string | null;
  dayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  maxStudents?: number;
  capacity?: number;
  enrolledCount?: number;
  schedule?: string;
  durationMin?: number;
  status?: string;
  isActive?: boolean;
}

export type ScheduleModality = "JIU_JITSU" | "BOXE_KICKBOXING" | "CAPOEIRA" | "MMA";
export type ScheduleDayOfWeek =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";
export type ScheduleLevel = "BEGINNER" | "INTERMEDIATE" | "ADVANCED" | "ALL_LEVELS";

export interface ScheduleEntryBase {
  title: string;
  modality: ScheduleModality;
  dayOfWeek: ScheduleDayOfWeek;
  startTime: string;
  endTime: string;
  instructorName: string;
  level: ScheduleLevel;
  location?: string | null;
  capacity?: number | null;
  active?: boolean;
  notes?: string | null;
}

export interface PublicScheduleEntry extends ScheduleEntryBase {
  id: string;
}

export interface AdminScheduleEntry extends ScheduleEntryBase {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export type CreateScheduleEntryRequest = ScheduleEntryBase;
export type UpdateScheduleEntryRequest = Partial<ScheduleEntryBase>;

export interface ClassEnrollment {
  id: string;
  studentId?: string;
  studentName?: string;
  userId?: string;
  userName?: string;
  classId: string;
  enrolledAt: string;
  attended?: boolean;
  cancelledAt?: string | null;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  phone: string;
  dateOfBirth?: string;
  age: number;
  parishOrArea: string;
  hasMartialArtsExperience: boolean;
  martialArtsExperienceDetails?: string;
  trainingGoal: string;
  preferredModality: PreferredModality;
  preferredModalityOther?: string;
  preferredTrainingTime: PreferredTrainingTime;
  preferredTrainingTimeOther?: string;
  preferredTrainingDays: PreferredTrainingDay[];
  valuesMartialArtsPhilosophy: boolean;
  preferredContactMethod: PreferredContactMethod;
  preferredContactMethodOther?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export type PreferredModality =
  | "KICKBOXING"
  | "JIU_JITSU"
  | "CAPOEIRA"
  | "BOXE"
  | "MMA"
  | "JIU_JITSU_KIDS"
  | "CAPOEIRA_KIDS"
  | "KICKBOXING_KIDS"
  | "OTHER";

export type PreferredTrainingTime =
  | "MORNING_BEFORE_0830"
  | "LUNCH_1230"
  | "AFTERNOON_14_17"
  | "NIGHT_AFTER_18"
  | "OTHER";

export type PreferredTrainingDay = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY";

export type PreferredContactMethod = "CALL" | "MESSAGE" | "OTHER";

export interface AdminPreRegistrationListItem {
  id: string;
  fullName: string;
  phone: string;
  age: number | null;
  parish: string | null;
  preferredModalities: string | null;
  preferredTrainingTimes: string | null;
  preferredTrainingDays: string | null;
  preferredContactMethod: string | null;
  submittedAt: string;
  status: string;
}

export interface AdminPreRegistrationDetail extends AdminPreRegistrationListItem {
  hasMartialArtsExperience: boolean | null;
  martialArtsExperienceDetails?: string;
  trainingGoal?: string;
  philosophyImportant: boolean | null;
  source: string;
  notes?: string;
  updatedAt: string;
  createdAt: string;
}

export interface AdminRegistration {
  id: string;
  userId: string | null;
  leadId: string | null;
  source: "CSV" | "SITE";
  fullName: string;
  email: string | null;
  phone: string;
  age: number | null;
  parish: string | null;
  hasMartialArtsExperience: boolean | null;
  martialArtsExperienceDetails: string | null;
  trainingGoal: string | null;
  preferredModalities: string | null;
  preferredModalityOther: string | null;
  preferredTrainingTimes: string | null;
  preferredTrainingTimeOther: string | null;
  preferredTrainingDays: string | null;
  philosophyImportant: boolean | null;
  preferredContactMethod: string | null;
  preferredContactMethodOther: string | null;
  submittedAt: string;
  status: string;
  notes: string | null;
  accountCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type GraduationModality = "JIU_JITSU" | "BOXE_KICKBOXING" | "CAPOEIRA" | "MMA";

export interface AdminGraduation {
  studentName: string;
  studentEmail: string;
  modality: GraduationModality;
  currentGraduation: string;
  nextGraduation: string;
  updatedAt: string | null;
}

export interface UpdateAdminGraduationRequest {
  studentEmail: string;
  modality: GraduationModality;
  currentLevel: string;
}

export interface AdminGraduationOption {
  id: string;
  name: string;
  levelOrder: number;
  modality: GraduationModality;
  martialArtId: string;
  martialArtName: string;
}

export interface UpdateStudentGraduationRequest {
  modality: GraduationModality;
  graduationId: string;
  reason: string;
}

export interface UpdateUserRequest {
  name?: string;
  phone?: string;
  avatarUrl?: string;
}

export interface CreateBeltRequest {
  name: string;
  colorHex?: string;
  rankOrder: number;
}

export interface CreatePlanRequest {
  name: string;
  description?: string | null;
  price: number;
  currency?: string;
  durationDays: number;
  maxClasses?: number | null;
  features?: string[];
  level?: string | null;
  instructor?: string | null;
  schedule?: string[] | null;
}

export type UpdatePlanRequest = Partial<CreatePlanRequest> & {
  isActive?: boolean;
};

export interface UpdateStudentProfileRequest {
  beltId?: string;
  trainingDays?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  medicalNotes?: string;
  recoveryNotes?: string;
  goals?: string;
  observations?: string;
}

export interface CreateStudentProfileRequest {
  userId: string;
  beltId?: string;
  trainingDays?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  medicalNotes?: string;
  recoveryNotes?: string;
  goals?: string;
  observations?: string;
}

export interface CreateAttendanceRequest {
  studentId: string;
  date: string;
  present: boolean;
  classId?: string;
  notes?: string;
}

export interface CreateMembershipRequest {
  userId: string;
  planId: string;
  startDate: string;
  autoRenew?: boolean;
}

export interface BookTrialRequest {
  name: string;
  email: string;
  phone?: string;
}

export interface TrialBookingResponse {
  id: string;
  name: string;
  email: string;
  phone?: string;
  program: string;
  status: string;
  createdAt: string;
}

export interface CreateContactRequest {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

export interface ContactResponse {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

export interface CheckoutRequest {
  name: string;
  email: string;
  password: string;
  planId: string;
  paymentMethod: string;
}

export interface PaymentFormRequest {
  phoneNumber?: string;
  cardHolderName?: string;
  cardNumber?: string;
  expirationDate?: string;
  cvv?: string;
  paymentId: string;
}

export interface CancelMyMembershipResponse {
  status:
    | "ACTIVE"
    | "EXPIRED"
    | "CANCELLED"
    | "SUSPENDED"
    | "PENDING_PAYMENT"
    | "PENDING_APPROVAL"
    | "REJECTED";
  cancellationScheduled: boolean;
  accessUntil: string;
  message: string;
}

export interface CheckoutResponse {
  id: string;
  name: string;
  email: string;
  planName: string;
  planPrice: number;
  paymentMethod: string;
  paymentStatus: string;
  message: string;
  userId?: string;
  accessToken?: string;
  refreshToken?: string;
  createdAt?: string;
}
